import React, { useState, useMemo } from "react";
import { 
  Users, 
  Search, 
  Calendar as CalendarIcon, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  AlertCircle, 
  Download, 
  Save, 
  UserCheck, 
  FileText,
  ChevronDown,
  ArrowRight,
  BookOpen,
  ArrowLeft,
  Video,
  MapPin
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "../ui/table";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "../ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { 
  Tooltip, 
  TooltipContent, 
  TooltipProvider, 
  TooltipTrigger 
} from "../ui/tooltip";
import { Progress } from "../ui/progress";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Calendar } from "../ui/calendar";
import { cn } from "../ui/utils";
import { toast } from "sonner";

type AttendanceStatus = "present" | "absent" | "late" | "half-day";

type Student = {
  id: number;
  admnNo: string;
  rollNo: string;
  name: string;
  img?: string;
  courseCode: string;
  status: AttendanceStatus;
  remarks: string;
  attendanceRate: number;
};

type Lecture = {
  id: string;
  courseName: string;
  courseCode: string;
  time: string;
  room: string;
  strength: number;
  section: string;
  faculty: string;
  program: string;
  session: string;
};

// Mock Lectures
const myLectures: Lecture[] = [
  { id: "L1", courseName: "Data Structures", courseCode: "CS001-IT", time: "09:00 AM - 10:30 AM", room: "Room 402", strength: 45, section: "Section A", faculty: "Faculty of CS", program: "BSCS", session: "FALL 22" },
  { id: "L2", courseName: "Database Systems", courseCode: "CS002-DS", time: "11:00 AM - 12:30 PM", room: "Lab 2", strength: 38, section: "Section B", faculty: "Faculty of CS", program: "BSCS", session: "FALL 22" },
  { id: "L3", courseName: "Web Development", courseCode: "CS301", time: "02:00 PM - 03:30 PM", room: "Room 105", strength: 40, section: "Section A", faculty: "Faculty of CS", program: "BSCS", session: "FALL 22" },
];

const initialStudents: Student[] = [
  { id: 1, admnNo: "16701", rollNo: "01", name: "Aastha Sharma", courseCode: "CS001-IT", status: "present", remarks: "", attendanceRate: 88, img: "https://api.dicebear.com/7.x/avataaars/svg?seed=Aastha" },
  { id: 2, admnNo: "16702", rollNo: "02", name: "Abhishek Verma", courseCode: "CS001-IT", status: "present", remarks: "", attendanceRate: 92, img: "https://api.dicebear.com/7.x/avataaars/svg?seed=Abhishek" },
  { id: 3, admnNo: "16703", rollNo: "03", name: "Akshay Choudhary", courseCode: "CS002-DS", status: "absent", remarks: "Doctor's appointment", attendanceRate: 74, img: "https://api.dicebear.com/7.x/avataaars/svg?seed=Akshay" },
  { id: 4, admnNo: "16704", rollNo: "04", name: "Ananya Rajpoot", courseCode: "CS001-IT", status: "present", remarks: "", attendanceRate: 95, img: "https://api.dicebear.com/7.x/avataaars/svg?seed=Ananya" },
  { id: 5, admnNo: "16705", rollNo: "05", name: "Ishaan Gupta", courseCode: "CS001-IT", status: "late", remarks: "Bus delay", attendanceRate: 82, img: "https://api.dicebear.com/7.x/avataaars/svg?seed=Ishaan" },
  { id: 6, admnNo: "16706", rollNo: "06", name: "Zara Khan", courseCode: "CS002-DS", status: "present", remarks: "", attendanceRate: 91, img: "https://api.dicebear.com/7.x/avataaars/svg?seed=Zara" },
];

export default function MarkAttendance() {
  const [selectedLecture, setSelectedLecture] = useState<Lecture | null>(null);
  const [date, setDate] = useState<Date>(new Date());
  const [searchTerm, setSearchTerm] = useState("");
  const [students, setStudents] = useState<Student[]>(initialStudents);
  const [isSaving, setIsSaving] = useState(false);

  const filteredStudents = useMemo(() => {
    return students.filter((student) => {
      const matchesLecture = selectedLecture ? student.courseCode === selectedLecture.courseCode : true;
      const matchesSearch =
        student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.admnNo.includes(searchTerm) ||
        student.rollNo.includes(searchTerm);

      return matchesLecture && matchesSearch;
    });
  }, [students, selectedLecture, searchTerm]);

  const stats = useMemo(() => {
    const total = filteredStudents.length;
    const present = filteredStudents.filter(s => s.status === "present").length;
    const absent = filteredStudents.filter(s => s.status === "absent").length;
    const late = filteredStudents.filter(s => s.status === "late").length;
    const halfDay = filteredStudents.filter(s => s.status === "half-day").length;
    const percentage = total > 0 ? Math.round(((present + late + halfDay * 0.5) / total) * 100) : 0;
    
    return { total, present, absent, late, halfDay, percentage };
  }, [filteredStudents]);

  const updateStatus = (id: number, status: AttendanceStatus) => {
    setStudents((prev) =>
      prev.map((student) =>
        student.id === id ? { ...student, status } : student
      )
    );
  };

  const updateRemarks = (id: number, remarks: string) => {
    setStudents((prev) =>
      prev.map((student) =>
        student.id === id ? { ...student, remarks } : student
      )
    );
  };

  const markAll = (status: AttendanceStatus) => {
    setStudents((prev) =>
      prev.map((student) => {
        if (!selectedLecture || student.courseCode === selectedLecture.courseCode) {
          return { ...student, status };
        }
        return student;
      })
    );
    toast.success(`Marked all as ${status.replace("-", " ")}`);
  };

  const handleSave = () => {
    setIsSaving(true);
    setTimeout(() => {
      setIsSaving(false);
      toast.success("Attendance synced successfully!", {
        description: `For ${selectedLecture?.courseName} on today's session`,
      });
    }, 1200);
  };

  // --- RENDER SELECTION VIEW ---
  if (!selectedLecture) {
    return (
      <div className="p-4 md:p-6 space-y-8 bg-background/50 min-h-screen page-enter">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold tracking-tight text-foreground/90 flex items-center gap-3">
             <div className="p-2 bg-primary/10 rounded-xl">
                <CalendarIcon className="h-7 w-7 text-primary" />
             </div>
             My Schedule
          </h1>
          <p className="text-muted-foreground font-medium text-sm ml-12">
            Select a class or lecture to start marking attendance for <span className="text-primary font-bold">today</span>
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {myLectures.map((lecture) => (
            <Card 
              key={lecture.id} 
              className="glass-card group cursor-pointer hover:border-primary/50 transition-all duration-500 overflow-hidden relative"
              onClick={() => setSelectedLecture(lecture)}
            >
              <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                 <BookOpen className="h-24 w-24 text-primary" />
              </div>
              
              <CardHeader className="pb-4 relative z-10">
                <div className="flex justify-between items-start mb-2">
                   <Badge variant="outline" className="glass-effect border-white/10 text-primary font-bold px-3 py-1 rounded-lg">
                      {lecture.courseCode}
                   </Badge>
                   <Badge className="bg-emerald-500/10 text-emerald-500 border-none hover:bg-emerald-500/20">
                      Ongoing
                   </Badge>
                </div>
                <CardTitle className="text-xl font-black tracking-tight group-hover:text-primary transition-colors">
                  {lecture.courseName}
                </CardTitle>
                <CardDescription className="font-bold text-muted-foreground/80 uppercase tracking-widest text-[10px] mt-1">
                   {lecture.section} • {lecture.program}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 relative z-10 pt-0">
                <div className="space-y-2">
                   <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                      <Clock className="h-4 w-4 text-primary/70" />
                      <span>{lecture.time}</span>
                   </div>
                   <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                      <MapPin className="h-4 w-4 text-primary/70" />
                      <span>{lecture.room}</span>
                   </div>
                   <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                      <Users className="h-4 w-4 text-primary/70" />
                      <span>{lecture.strength} Students Enrolled</span>
                   </div>
                </div>

                <div className="pt-4 flex items-center justify-between border-t border-white/5 group-hover:border-primary/20 transition-colors">
                   <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60 group-hover:opacity-100 transition-opacity">
                      Click to proceed
                   </p>
                   <div className="p-2 bg-primary/10 rounded-full group-hover:bg-primary group-hover:text-white transition-all transform group-hover:translate-x-1">
                      <ArrowRight className="h-4 w-4" />
                   </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Quick Stats Helper */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 opacity-60 pointer-events-none">
           {[
             { label: "Today's Total Lectures", value: "05", icon: Video },
             { label: "Attendance Completed", value: "02", icon: CheckCircle2 },
             { label: "Pending Sessions", value: "03", icon: AlertCircle },
             { label: "Overall Presence", value: "92%", icon: UserCheck },
           ].map((stat, i) => (
             <div key={i} className="flex flex-col p-4 rounded-2xl bg-white/5 border border-white/10">
                <stat.icon className="h-5 w-5 text-primary mb-2" />
                <span className="text-[10px] uppercase font-bold text-muted-foreground">{stat.label}</span>
                <span className="text-2xl font-black tracking-tight">{stat.value}</span>
             </div>
           ))}
        </div>
      </div>
    );
  }

  // --- RENDER MARKING VIEW ---
  return (
    <div className="p-4 md:p-6 space-y-6 bg-background/50 min-h-screen page-enter">
      {/* --- BREADCRUMBS / NAVIGATION --- */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div className="space-y-4">
          <Button 
            variant="ghost" 
            size="sm" 
            className="group px-0 hover:bg-transparent text-primary font-bold flex items-center gap-2 mb-2"
            onClick={() => setSelectedLecture(null)}
          >
            <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
            Back to My Schedule
          </Button>

          <div className="space-y-1">
            <h1 className="text-3xl font-black tracking-tight text-foreground/90 flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-xl">
                 <UserCheck className="h-7 w-7 text-primary" />
              </div>
              {selectedLecture.courseName}
            </h1>
            <p className="text-muted-foreground font-medium text-[13px] ml-12">
               Class Session: <span className="text-primary font-bold">{selectedLecture.time}</span> • Room <span className="text-primary font-bold">{selectedLecture.room}</span>
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Button variant="outline" className="glass-effect-premium border-white/10 h-11 px-4">
            <Download className="h-4 w-4 mr-2" />
            Export sheet
          </Button>
          <Button 
            className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg shadow-primary/20 h-11 px-6 font-bold"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? "Syncing..." : "Commit Attendance"}
            {!isSaving && <Save className="ml-2 h-4 w-4" />}
          </Button>
        </div>
      </div>

      {/* --- STATS OVERVIEW --- */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {[
          { label: "Class Strength", value: stats.total, icon: Users, color: "text-blue-500", bg: "bg-blue-500/10" },
          { label: "Present Today", value: stats.present, icon: CheckCircle2, color: "text-emerald-500", bg: "bg-emerald-500/10" },
          { label: "Absent Today", value: stats.absent, icon: XCircle, color: "text-rose-500", bg: "bg-rose-500/10" },
          { label: "Late / Half", value: `${stats.late} / ${stats.halfDay}`, icon: Clock, color: "text-amber-500", bg: "bg-amber-500/10" },
          { label: "Session Presence", value: `${stats.percentage}%`, icon: AlertCircle, color: "text-primary", bg: "bg-primary/10", full: true },
        ].map((item, i) => (
          <Card key={i} className={cn("glass-card border-white/5", item.full && "hidden lg:block")}>
            <CardContent className="p-4 flex items-center gap-4">
              <div className={cn("p-3 rounded-xl", item.bg)}>
                <item.icon className={cn("h-5 w-5", item.color)} />
              </div>
              <div>
                 <p className="text-[10px] uppercase font-bold tracking-widest text-muted-foreground">{item.label}</p>
                 <p className="text-xl font-bold tracking-tight">{item.value}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* --- FILTERS & CONTROL BAR --- */}
      <Card className="glass-card border-white/10 shadow-xl overflow-visible">
        <CardContent className="p-4 md:p-6 grid grid-cols-1 md:grid-cols-5 lg:grid-cols-6 gap-4 items-end">
          
          <div className="space-y-2 lg:col-span-3">
            <label className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-1.5 ml-1">
              <CalendarIcon className="h-3 w-3" /> Attendance Date & Time
            </label>
            <div className="flex gap-2">
               <Popover>
                  <PopoverTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="flex-1 justify-start text-left font-semibold glass-effect h-11 border-white/10 hover:bg-white/10 hover:border-white/20 transition-all rounded-xl shadow-lg ring-offset-background"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 text-primary animate-pulse" />
                      <span className="truncate">
                        {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                      <ChevronDown className="ml-auto h-4 w-4 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent 
                    className="w-fit p-4 glass-card border-white/10 shadow-2xl backdrop-blur-3xl rounded-2xl animate-in fade-in zoom-in duration-200" 
                    align="start"
                  >
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={(d) => d && setDate(d)}
                      initialFocus
                      className="bg-transparent"
                      classNames={{
                        day_selected: "bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg shadow-lg shadow-primary/30 font-bold",
                        day_today: "bg-white/10 text-primary font-black rounded-lg",
                      }}
                    />
                  </PopoverContent>
                </Popover>
                
                <div className="flex-1 glass-effect h-11 border-white/10 rounded-xl px-4 flex items-center justify-between text-muted-foreground font-medium text-sm">
                   <span>Session: {selectedLecture.time}</span>
                   <Clock className="h-4 w-4 opacity-40" />
                </div>
            </div>
          </div>

          <div className="space-y-2 lg:col-span-2">
            <label className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground ml-1">Search Students</label>
            <div className="relative group">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground group-focus-within:text-primary transition-colors" />
              <Input 
                placeholder="Name, Reg No, Roll No..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 glass-effect h-11 border-white/10 focus-visible:ring-primary/50 text-sm font-medium"
              />
            </div>
          </div>

          <div className="flex gap-2 h-11">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="icon" className="h-full w-full max-w-[50px] shrink-0 glass-effect border-white/10" onClick={() => markAll("present")}>
                    <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Mark All Present</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="icon" className="h-full w-full max-w-[50px] shrink-0 glass-effect border-white/10" onClick={() => markAll("absent")}>
                    <XCircle className="h-4 w-4 text-rose-500" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Mark All Absent</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

        </CardContent>
      </Card>

      {/* --- ATTENDANCE LIST --- */}
      <Card className="glass-card border-white/10 shadow-2xl overflow-hidden min-h-[400px]">
        <Table>
          <TableHeader className="bg-muted/50 border-white/5">
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-[80px] text-center font-bold uppercase tracking-widest text-[11px]">Roll</TableHead>
              <TableHead className="font-bold uppercase tracking-widest text-[11px]">Student Info</TableHead>
              <TableHead className="text-center font-bold uppercase tracking-widest text-[11px]">Attendance Rate</TableHead>
              <TableHead className="text-center font-bold uppercase tracking-widest text-[11px]">Session Status</TableHead>
              <TableHead className="font-bold uppercase tracking-widest text-[11px]">Remarks</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredStudents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="h-64 text-center">
                  <div className="flex flex-col items-center justify-center space-y-3 opacity-50">
                    <Users className="h-12 w-12" />
                    <p className="font-medium text-lg">No students found for this selection</p>
                    <p className="text-sm">Try adjusting your search term</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              filteredStudents.map((student) => (
                <TableRow key={student.id} className="group hover:bg-white/[0.03] transition-colors border-white/5">
                  <TableCell className="text-center font-mono font-bold text-muted-foreground/80 group-hover:text-primary transition-colors">
                    {student.rollNo}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10 border border-white/10">
                        <AvatarImage src={student.img} />
                        <AvatarFallback className="bg-primary/10 text-primary font-bold">
                          {student.name.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex flex-col">
                        <span className="font-bold text-[13px] tracking-tight group-hover:text-primary transition-colors">
                          {student.name}
                        </span>
                        <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                          {student.admnNo}
                        </span>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="w-[180px]">
                    <div className="space-y-1.5 px-4">
                      <div className="flex justify-between text-[10px] font-bold uppercase tracking-tighter">
                        <span className="text-muted-foreground">Overall</span>
                        <span className={cn(
                          student.attendanceRate < 75 ? "text-rose-500" : "text-emerald-500"
                        )}>{student.attendanceRate}%</span>
                      </div>
                      <Progress 
                        value={student.attendanceRate} 
                        className={cn("h-1.5", student.attendanceRate < 75 ? "[&>div]:bg-rose-500" : "[&>div]:bg-emerald-500")} 
                      />
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-center items-center gap-1">
                       <div className="inline-flex items-center p-1 rounded-xl bg-white/5 border border-white/10 group-hover:bg-white/10 transition-all">
                          {[
                            { id: "present", label: "P", color: "text-emerald-500", hoverBg: "hover:bg-emerald-500/20" },
                            { id: "absent", label: "A", color: "text-rose-500", hoverBg: "hover:bg-rose-500/20" },
                            { id: "late", label: "L", color: "text-amber-500", hoverBg: "hover:bg-amber-500/20" },
                            { id: "half-day", label: "H", color: "text-blue-500", hoverBg: "hover:bg-blue-500/20" },
                          ].map((opt) => {
                            const isActive = student.status === opt.id;
                            return (
                              <TooltipProvider key={opt.id}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      onClick={() => updateStatus(student.id, opt.id as AttendanceStatus)}
                                      className={cn(
                                        "h-8 w-8 flex items-center justify-center rounded-lg transition-all duration-300 font-bold text-[10px]",
                                        isActive 
                                          ? cn("scale-110 shadow-lg text-white ring-2 ring-white/10", 
                                              opt.id === "present" && "bg-emerald-500 shadow-emerald-500/40",
                                              opt.id === "absent" && "bg-rose-500 shadow-rose-500/40",
                                              opt.id === "late" && "bg-amber-500 shadow-amber-500/40",
                                              opt.id === "half-day" && "bg-blue-500 shadow-blue-500/40")
                                          : cn("text-muted-foreground/40", opt.hoverBg, "hover:text-foreground hover:scale-105")
                                      )}
                                    >
                                      {opt.label}
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent side="top">
                                    {opt.id.charAt(0).toUpperCase() + opt.id.slice(1).replace("-", " ")}
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            );
                          })}
                       </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="relative group/remark">
                      <FileText className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground opacity-50 group-hover/remark:opacity-100 transition-opacity" />
                      <Input 
                        placeholder="Add note..." 
                        value={student.remarks}
                        onChange={(e) => updateRemarks(student.id, e.target.value)}
                        className="bg-transparent border-transparent hover:bg-white/5 focus:bg-white/10 border-b-border h-8 text-[12px] pl-9 group-focus-within/remark:border-primary transition-all rounded-md font-medium"
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* --- FOOTER FEEDBACK --- */}
      <div className="flex items-center justify-between text-muted-foreground/60 px-2 py-4">
         <div className="flex items-center gap-4 text-[10px] font-bold uppercase tracking-widest">
            <span className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-emerald-500" /> Present</span>
            <span className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-rose-500" /> Absent</span>
            <span className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-amber-500" /> Late</span>
            <span className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-blue-500" /> Half Day</span>
         </div>
         <p className="text-[10px] font-bold uppercase tracking-widest flex items-center gap-2">
            Auto-save enabled <ArrowRight className="h-3 w-3" /> <span className="text-secondary font-black">XEROALPHA SYNC</span>
         </p>
      </div>
    </div>
  );
}
